'use strict';

import { Entity } from './Entity';
import { User } from './User/User';
import { Utilities } from '../Utilities/Utilities';
import { Ind } from './Ind/Ind';
import type { CalendarEventPayload } from '../Repositories/CalendarEventRepository';
import type { Operator } from '../Model/Operator';

export enum CalendarEventType {
  External = 'external',
  Internal = 'internal',
}

export class CalendarEvent extends Entity {
  protected id: number;
  protected name: string;
  protected user: User;
  protected inds: Array<Ind>;
  protected description: string;
  protected location: string;
  protected dateTimeStart: Date;
  protected dateTimeEnd: Date;
  protected vendorID: string;
  protected type: CalendarEventType;
  protected _isAllDayEvent: boolean;
  protected creator: Operator;

  constructor() {
    super();
  }

  public hydrate(data: CalendarEventPayload) {
    this.id = data.id;
    this.name = data.name;
    this.description = data.description;
    this._isAllDayEvent = data.isAllDayEvent;
    this.location = data.location;
    this.type = data.type as CalendarEventType;
    this.creator = data.creator;

    if (data.user) {
      const user = new User();
      user.hydrate(data.user);
      this.user = user;
    }

    if (data.inds) {
      const inds: Array<Ind> = [];

      data.inds.forEach((indData) => {
        const ind = new Ind();
        ind.hydrate(indData);
        inds.push(ind);
      });

      this.inds = inds;
    }

    if (data.dateTimeStart) {
      const dateTimeStart = Utilities.dateTimeStringToDateTime(data.dateTimeStart);
      this.dateTimeStart = dateTimeStart;
    }

    if (data.dateTimeEnd) {
      const dateTimeEnd = Utilities.dateTimeStringToDateTime(data.dateTimeEnd);
      this.dateTimeEnd = dateTimeEnd;
    }
  }

  public getId(): number {
    return this.id;
  }

  public getUser(): User {
    return this.user;
  }

  public setUser(user: User): this {
    this.user = user;
    return this;
  }

  public getInds(): Array<Ind> {
    return this.inds;
  }

  public setInds(inds: Array<Ind>): this {
    this.inds = inds;
    return this;
  }

  public addInd(ind: Ind): this {
    if (!this.inds) {
      this.inds = [];
    }

    this.inds.push(ind);
    return this;
  }

  public getName(): string {
    return this.name;
  }

  public setName(name: string): this {
    this.name = name;
    return this;
  }

  public getType(): CalendarEventType {
    return this.type;
  }

  public setType(type: CalendarEventType): this {
    this.type = type;
    return this;
  }

  public getVendorID(): string {
    return this.vendorID;
  }

  public setVendorID(vendorID: string): this {
    this.vendorID = vendorID;
    return this;
  }

  public getDescription(): string {
    return this.description;
  }

  public setDescription(description: string): this {
    this.description = description;
    return this;
  }

  public getLocation(): string {
    return this.location;
  }

  public setLocation(location: string): this {
    this.location = location;
    return this;
  }

  public getCreator(): Operator {
    return this.creator;
  }

  public setCreator(creator: Operator): this {
    this.creator = creator;
    return this;
  }

  public getDateTimeStart(): Date {
    return this.dateTimeStart;
  }

  public setDateTimeStart(dateTimeStart: Date): this {
    this.dateTimeStart = dateTimeStart;
    return this;
  }

  public getDateTimeEnd(): Date {
    return this.dateTimeEnd;
  }

  public setDateTimeEnd(dateTimeEnd: Date): this {
    this.dateTimeEnd = dateTimeEnd;
    return this;
  }

  public isAllDayEvent(): boolean {
    return this._isAllDayEvent;
  }

  public setIsAllDayEvent(isAllDayEvent: boolean): this {
    this._isAllDayEvent = isAllDayEvent;
    return this;
  }
}
